import {useEffect,useMemo,useRef,useState} from 'react';

/** Owner FloatHelm Room 2 screenshots (Oct 30): measurements graph cards,
 * room-specific Logbook with Restore, and Task/project list.
 * Each tab persists through the same authenticated room-scoped API.
 */
type Mode='Measurements'|'Logbook'|'Tasks';
type Definition={key:string;label:string;unit:string};
type Reading={id:string;resourceId:string;resourceName:string;color:string|null;
  metricKey:string;value:number;measuredAt:string};
type Entry={id:string;content:string;authorName:string;authorId:string;
  createdAt:string;isMine:boolean;
  comments:Array<{id:string;authorName:string;content:string;createdAt:string}>};
type Task={id:string;title:string;description:string;assigneeName:string|null;
  isMine:boolean;dueAt:string|null;status:string;createdAt:string};
type Data={definitions:Definition[];readings:Reading[];entries:Entry[];
  draft:string;tasks:Task[]};
const day=(offset:number)=>new Date(Date.now()+offset*86400000).toISOString().slice(0,10);
const time=(iso:string)=>new Intl.DateTimeFormat('en-US',{month:'short',day:'numeric',
  year:'numeric',hour:'numeric',minute:'2-digit'}).format(new Date(iso));
async function json<T>(url:string,options?:RequestInit):Promise<T>{
  const response=await fetch(url,{credentials:'same-origin',cache:'no-store',...options});
  if(!response.ok)throw Error(response.status===401?'Please sign in':
    response.status===403?'Room access denied':'Unable to save or load room records');
  const body=await response.json() as {ok:boolean;data:T};
  if(!body.ok)throw Error('Room records unavailable');
  return body.data;
}
function MiniGraph({points,color='#8494a5'}:{points:Reading[];color?:string}){
  if(!points.length)return <div className="ecfs-measure-no-data">No Data</div>;
  const ordered=[...points].sort((a,b)=>Date.parse(a.measuredAt)-Date.parse(b.measuredAt));
  const values=ordered.map(p=>p.value);
  const lo=Math.min(...values),hi=Math.max(...values),span=Math.max(hi-lo,0.001);
  const x=(i:number)=>14+(i/Math.max(1,ordered.length-1))*178;
  const y=(v:number)=>64-(v-lo)/span*46;
  return <svg viewBox="0 0 205 85" role="img"
    aria-label={ordered.length+' recorded values'}>
    <path d="M12 9 V66 H196" fill="none" stroke="currentColor" opacity=".35"/>
    <path d="M12 30 H196 M12 48 H196" fill="none" stroke="currentColor"
      strokeDasharray="2 3" opacity=".17"/>
    {ordered.length>1&&<polyline fill="none" stroke={color} strokeWidth="1.8"
      points={ordered.map((p,i)=>x(i)+','+y(p.value)).join(' ')}/>}
    {ordered.map((p,i)=><circle key={p.id} cx={x(i)} cy={y(p.value)} r="3.4"
      fill="white" stroke={p.color??color} strokeWidth="1.8">
      <title>{p.resourceName+': '+p.value+' on '+time(p.measuredAt)}</title>
    </circle>)}
    <text x="13" y="79" fontSize="8" fill="currentColor">
      {new Date(ordered[0].measuredAt).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
    </text>
    <text x="147" y="79" fontSize="8" fill="currentColor">
      {new Date(ordered[ordered.length-1].measuredAt).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
    </text>
  </svg>;
}
function RoomComparison({points,unit}:{points:Reading[];unit:string}){
  const rooms=[...new Map(points.map(p=>[p.resourceId,p.resourceName])).entries()];
  if(!rooms.length)return <p>No readings for the selected date range.</p>;
  const min=Math.min(...points.map(p=>p.value)),max=Math.max(...points.map(p=>p.value));
  const range=Math.max(1,max-min);
  const width=Math.max(340,rooms.length*115+70);
  const cx=(id:string)=>56+(rooms.findIndex(r=>r[0]===id)+.5)*(width-75)/rooms.length;
  const cy=(v:number)=>160-(v-min)/range*116;
  return <div className="ecfs-room-comparison-scroll">
    <svg viewBox={'0 0 '+width+' 215'} role="img"
      aria-label="Measurement readings by room, colored by each room's assigned color">
      <path d={'M40 22 V170 H'+(width-22)} fill="none" stroke="currentColor" opacity=".5"/>
      {[0,1,2,3,4].map(i=><g key={i}>
        <path d={'M40 '+(160-i*29)+' H'+(width-22)} fill="none"
          stroke="currentColor" opacity=".14"/>
        <text x="37" y={164-i*29} fontSize="10" textAnchor="end"
          fill="currentColor">{(min+i*range/4).toFixed(2)}</text>
      </g>)}
      {rooms.map(([id,name])=><text key={id} x={cx(id)} y="189" fontSize="11"
        textAnchor="middle" fill="currentColor">{name.length>18?name.slice(0,17)+'…':name}</text>)}
      {points.map((p,i)=><circle key={p.id} cx={cx(p.resourceId)+(i%3-1)*5}
        cy={cy(p.value)} r="4.5" fill={p.color??'#8293a7'} stroke="currentColor"
        strokeWidth=".55">
        <title>{p.resourceName+' · '+p.value+' '+unit+' · '+time(p.measuredAt)}</title>
      </circle>)}
      <text x={width/2} y="209" textAnchor="middle" fontSize="10"
        fill="currentColor">Rooms within selected date range</text>
    </svg>
  </div>;
}
export function RoomOperationsWorkspace({mode,roomId,roomName,locationCode}:{
  mode:Mode;roomId:string;roomName:string;locationCode:string;
}){
  const [from,setFrom]=useState(()=>day(-30));
  const [to,setTo]=useState(()=>day(0));
  const [data,setData]=useState<Data|null>(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  const [message,setMessage]=useState('');
  const [refresh,setRefresh]=useState(0);
  const [selectedMetric,setSelectedMetric]=useState<string|null>(null);
  const [entryMetric,setEntryMetric]=useState('PH_LEVEL');
  const [entryValue,setEntryValue]=useState('');
  const [showTake,setShowTake]=useState(false);
  const [logText,setLogText]=useState('');
  const [draftIsDirty,setDraftIsDirty]=useState(false);
  const [commentDrafts,setCommentDrafts]=useState<Record<string,string>>({});
  const [taskTitle,setTaskTitle]=useState('');
  const [taskDescription,setTaskDescription]=useState('');
  const [due,setDue]=useState('');
  const [tasksScope,setTasksScope]=useState<'mine'|'all'>('mine');
  const [dueScope,setDueScope]=useState<'any'|'overdue'>('any');
  const [taskSearch,setTaskSearch]=useState('');
  const [showClosed,setShowClosed]=useState(false);
  const [showCreateTask,setShowCreateTask]=useState(false);
  const [busy,setBusy]=useState(false);
  const savedDraftRef=useRef('');
  const textRef=useRef('');
  const postedRef=useRef(false);
  const draftPromiseRef=useRef<Promise<unknown>>(Promise.resolve());
  const path='/api/v1/staff/rooms/'+encodeURIComponent(roomId)+'/operations';
  const write=(operation:object,keepalive=false)=>json<{saved:boolean}>(path,{
    method:'POST',keepalive,headers:{'content-type':'application/json'},
    body:JSON.stringify({location:locationCode,operation}),
  });
  useEffect(()=>{
    let mounted=true;
    setLoading(true);setError('');
    void json<Data>(path+'?'+new URLSearchParams({
      location:locationCode,from,to,
    })).then(result=>{
      if(!mounted)return;
      setData(result);setLoading(false);
      savedDraftRef.current=result.draft;
      if(!draftIsDirty)setLogText('');
    }).catch(e=>{if(mounted){setLoading(false);setError(String(e));}});
    return ()=>{mounted=false;};
  // Reload when room/date/after write changes, not on draft keystrokes.
  },[path,locationCode,from,to,refresh]);
  useEffect(()=>{
    textRef.current=logText;
    if(!draftIsDirty)return;
    const handle=setTimeout(()=>{
      if(postedRef.current)return;
      const content=textRef.current;
      draftPromiseRef.current=write({kind:'draft',content}).then(()=>{
        savedDraftRef.current=content;
      }).catch(()=>setMessage('Draft could not be saved. Try again.'));
    },350);
    return ()=>clearTimeout(handle);
  },[logText,draftIsDirty,locationCode,roomId]);
  useEffect(()=>()=>{if(!postedRef.current&&textRef.current){
    void write({kind:'draft',content:textRef.current},true).catch(()=>undefined);
  }},[roomId,locationCode]);
  const submit=async(op:object,success:string,after?:()=>void)=>{
    setBusy(true);setMessage('');
    try{await write(op);setMessage(success);after?.();setRefresh(x=>x+1);}
    catch(e){setMessage(e instanceof Error?e.message:'Unable to save');}
    finally{setBusy(false);}
  };
  const relevant=useMemo(()=>data?.readings.filter(m=>m.resourceId===roomId)??[],
    [data,roomId]);
  const selected=data?.definitions.find(d=>d.key===selectedMetric);
  const shownTasks=(data?.tasks??[]).filter(t=>
    (showClosed||t.status==='OPEN')&&(tasksScope==='all'||t.isMine)&&
    (dueScope==='any'||Boolean(t.dueAt&&Date.parse(t.dueAt)<Date.now()))&&
    (t.title+' '+t.description).toLowerCase().includes(taskSearch.toLowerCase()));
  const restoreDraft=()=>{
    setLogText(savedDraftRef.current);setDraftIsDirty(true);
    setMessage('Saved draft restored.');
  };
  const postLog=async()=>{
    if(!logText.trim())return;
    postedRef.current=true;
    await draftPromiseRef.current;
    await submit({kind:'entry',content:logText},'Posted to the room Logbook.',()=>{
      setLogText('');textRef.current='';savedDraftRef.current='';
      setDraftIsDirty(false);
    });
    postedRef.current=false;
  };
  return <section className="ecfs-room-operations" aria-label={mode+' for '+roomName}>
    {error&&<p role="alert">{error}</p>}
    {message&&<p role="status">{message}</p>}
    {loading&&<p role="status">Loading {mode.toLowerCase()}…</p>}
    {data&&mode==='Measurements'&&<>
      <div className="ecfs-room-week-controls">
        <h3>Measurements</h3>
        <button type="button" onClick={()=>setShowTake(v=>!v)}>Take Measurements</button>
      </div>
      {showTake&&<form aria-label="Take room measurements" onSubmit={e=>{
        e.preventDefault();const value=Number(entryValue);
        if(!entryValue.trim()||!Number.isFinite(value))return;
        void submit({kind:'reading',metricKey:entryMetric,value},
          'Measurement recorded.',()=>{setEntryValue('');setShowTake(false);});
      }}>
        <label>Measurement
          <select value={entryMetric} onChange={e=>setEntryMetric(e.target.value)}>
            {data.definitions.map(d=><option key={d.key} value={d.key}>
              {d.label}{d.unit?' ('+d.unit+')':''}</option>)}
          </select>
        </label>
        <label>Reading <input type="number" step="any" required
          value={entryValue} onChange={e=>setEntryValue(e.target.value)}/></label>
        <button type="submit" disabled={busy}>Save reading</button>
        <button type="button" onClick={()=>setShowTake(false)}>Cancel</button>
      </form>}
      <div className="ecfs-room-week-controls">
        <label>From <input type="date" value={from} max={to}
          onChange={e=>setFrom(e.target.value)}/></label>
        <label>To <input type="date" value={to} min={from}
          onChange={e=>setTo(e.target.value)}/></label>
      </div>
      {selected&&<section className="ecfs-room-metric-detail" aria-label={selected.label+' detailed graph'}>
        <button type="button" onClick={()=>setSelectedMetric(null)}>Close graph</button>
        <h4>{selected.label}{selected.unit?' · '+selected.unit:''}</h4>
        <RoomComparison points={data.readings.filter(p=>p.metricKey===selected.key)}
          unit={selected.unit}/>
      </section>}
      <div className="ecfs-room-metric-grid">
        {data.definitions.map(d=><button type="button" key={d.key}
          className="ecfs-room-metric-card" onClick={()=>setSelectedMetric(d.key)}>
          <strong>{d.label}</strong>{d.unit&&<small>{d.unit}</small>}
          <MiniGraph points={relevant.filter(m=>m.metricKey===d.key)}/>
        </button>)}
      </div>
    </>}
    {data&&mode==='Logbook'&&<>
      <form className="ecfs-room-logbook-compose" aria-label="Write room Logbook"
        onSubmit={e=>{e.preventDefault();void postLog();}}>
        <textarea rows={4} aria-label="Write a room Logbook entry"
          placeholder={'Post to '+roomName+' Logbook'} value={logText}
          onChange={e=>{setLogText(e.target.value);setDraftIsDirty(true);}}
          onBlur={()=>{if(textRef.current&&!postedRef.current){
            draftPromiseRef.current=write({kind:'draft',content:textRef.current})
              .then(()=>{savedDraftRef.current=textRef.current;})
              .catch(()=>setMessage('Draft could not be saved.'));}}}/>
        <div className="ecfs-room-logbook-actions">
          <button type="submit" disabled={busy||!logText.trim()}>Post</button>
          {Boolean(savedDraftRef.current)&&
            <button type="button" onClick={restoreDraft}>Restore</button>}
          <span>Drafts are saved to your own room Logbook account.</span>
        </div>
      </form>
      <div className="ecfs-room-logbook-feed">
        {data.entries.length===0&&<p>No Logbook entries yet.</p>}
        {data.entries.map(entry=><article key={entry.id}>
          <header><strong>{entry.authorName}</strong> · {time(entry.createdAt)}
            {entry.isMine&&<button type="button" disabled={busy}
              onClick={()=>void submit({kind:'delete-entry',entryId:entry.id},
                'Logbook entry removed.')}>Delete</button>}
          </header>
          <p>{entry.content}</p>
          {entry.comments.map(comment=><p key={comment.id} className="ecfs-room-logbook-comment">
            <strong>{comment.authorName}</strong> · {time(comment.createdAt)}
            <span>{comment.content}</span>
          </p>)}
          <form onSubmit={e=>{e.preventDefault();
            const content=commentDrafts[entry.id]??'';
            if(!content.trim())return;
            void submit({kind:'comment',entryId:entry.id,content},
              'Comment posted.',()=>setCommentDrafts(old=>({...old,[entry.id]:''})));
          }}>
            <input aria-label={'Comment on '+entry.authorName+' post'}
              value={commentDrafts[entry.id]??''} placeholder="Comment…"
              onChange={e=>setCommentDrafts(old=>({...old,[entry.id]:e.target.value}))}/>
            <button type="submit" disabled={busy}>Comment</button>
          </form>
        </article>)}
      </div>
    </>}
    {data&&mode==='Tasks'&&<>
      <div className="ecfs-room-task-filters">
        <select aria-label="Task assignment filter" value={tasksScope}
          onChange={e=>setTasksScope(e.target.value as 'mine'|'all')}>
          <option value="mine">Open to Me</option><option value="all">All Room Tasks</option>
        </select>
        <select aria-label="Task due date filter" value={dueScope}
          onChange={e=>setDueScope(e.target.value as 'any'|'overdue')}>
          <option value="any">Due Any Time</option><option value="overdue">Overdue</option>
        </select>
        <button type="button" onClick={()=>setShowClosed(v=>!v)}>
          {showClosed?'Open Only':'Show All'}</button>
        <input aria-label="Search room tasks" placeholder="Search"
          value={taskSearch} onChange={e=>setTaskSearch(e.target.value)}/>
        <button type="button" onClick={()=>{
          const csv=[['Project','Task','Due','Assignees','Status'],...shownTasks.map(t=>
            [roomName,t.title,t.dueAt??'',t.assigneeName??'',t.status])]
            .map(r=>r.map(v=>'"'+v.replaceAll('"','""')+'"').join(',')).join('\r\n');
          const blob=new Blob([csv],{type:'text/csv'});
          const url=URL.createObjectURL(blob);const a=document.createElement('a');
          a.href=url;a.download='room-tasks.csv';a.click();URL.revokeObjectURL(url);
        }}>CSV</button>
        <button type="button" onClick={()=>setShowCreateTask(v=>!v)}>New Task</button>
      </div>
      {showCreateTask&&<form aria-label="New room task" onSubmit={e=>{
        e.preventDefault();
        void submit({kind:'task',title:taskTitle,description:taskDescription,
          dueAt:due?new Date(due+'T12:00:00').toISOString():null},
          'Task created.',()=>{setTaskTitle('');setTaskDescription('');
            setDue('');setShowCreateTask(false);});
      }}>
        <label>Task <input required maxLength={240} value={taskTitle}
          onChange={e=>setTaskTitle(e.target.value)}/></label>
        <label>Details <textarea maxLength={4000} value={taskDescription}
          onChange={e=>setTaskDescription(e.target.value)}/></label>
        <label>Due date <input type="date" value={due}
          onChange={e=>setDue(e.target.value)}/></label>
        <button type="submit" disabled={busy}>Create Task</button>
      </form>}
      <table className="ecfs-room-task-table">
        <thead><tr><th>Project</th><th>Task</th><th>Due</th><th>Assignees</th></tr></thead>
        <tbody>{shownTasks.map(t=><tr key={t.id}>
          <td>{roomName}</td>
          <td><label><input type="checkbox" checked={t.status==='COMPLETED'}
            disabled={busy||!t.isMine} aria-label={'Complete '+t.title}
            onChange={e=>void submit({kind:'complete-task',taskId:t.id,
              completed:e.target.checked},'Task updated.')}/>
            {t.title}</label>{t.description&&<p>{t.description}</p>}</td>
          <td>{t.dueAt?new Date(t.dueAt).toLocaleDateString():'-'}</td>
          <td>{t.assigneeName??'-'}</td>
        </tr>)}
        {shownTasks.length===0&&<tr><td colSpan={4}>No matching room tasks.</td></tr>}
        </tbody>
      </table>
    </>}
  </section>;
}
